/* eslint-disable react/no-unescaped-entities */
import { useEffect, useRef, useState } from 'react';
import {
  FileText,
  Users,
  Scale,
  Coins,
  Search,
  FileSearch,
  CheckCircle2,
} from 'lucide-react';

/**
 * Premium analysis-in-progress screen.
 *
 * What it does (purely cosmetic — no business logic, no API calls):
 *   - Animates a progress bar with a deliberately tuned curve:
 *       0 → 85 %  fast  (~28 s)
 *       85 → 99 % slow  (asymptotic, never reaches 100 % on its own)
 *   - Cycles through 7 stages with a soft cross-fade transition.
 *   - When `isReady` flips true, jumps to 100 % and shows the success line
 *     for ~600 ms before the parent navigates the user away.
 *
 * Props:
 *   - active       boolean — when true the screen is mounted and animating.
 *   - isReady      boolean — flip this once the backend responded.
 *   - isUploading  boolean — true while the file is still being POSTed
 *                            (we stay on stage 1 + a sub-message in that case).
 *
 * The parent decides when to unmount this screen (usually 600 ms after
 * setting isReady = true, then navigating).
 */
const STAGES = [
  { Icon: FileText,      label: 'Lecture du document' },
  { Icon: Users,         label: 'Identification des personnes concernées' },
  { Icon: Scale,         label: 'Analyse des informations juridiques' },
  { Icon: Coins,         label: 'Analyse des biens, montants et droits' },
  { Icon: Search,        label: 'Vérification de la cohérence des informations' },
  { Icon: FileSearch,    label: 'Génération de votre synthèse personnalisée' },
  { Icon: CheckCircle2,  label: 'Finalisation de votre analyse' },
];

// Cumulative ms thresholds at which each stage becomes active.
// Tuned so stages 1-6 unroll within ~28 s; stage 7 is the steady state.
const STAGE_THRESHOLDS_MS = [0, 3500, 8000, 13000, 17500, 21500, 28000];

// Total ms expected to reach the 85 % milestone before slowing down.
const FAST_PHASE_MS = 28000;

const easeOutCubic = (t) => 1 - Math.pow(1 - t, 3);

const AnalysisLoadingScreen = ({ active, isReady, isUploading }) => {
  const [progress, setProgress] = useState(0);
  const [stageIndex, setStageIndex] = useState(0);
  const [showLongRunningHint, setShowLongRunningHint] = useState(false);
  const startedAtRef = useRef(null);
  const rafRef = useRef(null);

  // Reset & drive the animation while active.
  useEffect(() => {
    if (!active) {
      // Cleanup when the parent unmounts/disables us.
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
      startedAtRef.current = null;
      setProgress(0);
      setStageIndex(0);
      setShowLongRunningHint(false);
      return undefined;
    }

    if (startedAtRef.current === null) {
      startedAtRef.current = performance.now();
    }

    const tick = (now) => {
      const elapsed = now - startedAtRef.current;

      // ─ Progress curve ───────────────────────────────────────────────
      let next;
      if (elapsed <= FAST_PHASE_MS) {
        // 0 → 85 % using ease-out so it visibly accelerates then settles
        const t = elapsed / FAST_PHASE_MS;
        next = easeOutCubic(t) * 85;
      } else {
        // 85 → 99 % asymptotic, never crosses 99
        const slowElapsed = elapsed - FAST_PHASE_MS;
        // 14 % to cover, half-life ~25 s
        next = 99 - 14 * Math.exp(-slowElapsed / 25000);
      }

      // Hard cap at 99 % unless backend says ready (handled below)
      next = Math.min(next, 99);
      setProgress((p) => (next > p ? next : p));

      // ─ Stage selection ──────────────────────────────────────────────
      let idx = 0;
      for (let i = STAGE_THRESHOLDS_MS.length - 1; i >= 0; i -= 1) {
        if (elapsed >= STAGE_THRESHOLDS_MS[i]) {
          idx = i;
          break;
        }
      }
      setStageIndex(idx);

      // ─ Long-running hint ────────────────────────────────────────────
      // Stuck on the last stage for more than 8 s → soft reassurance.
      if (idx === STAGES.length - 1 && elapsed - STAGE_THRESHOLDS_MS[6] > 8000) {
        setShowLongRunningHint(true);
      }

      rafRef.current = requestAnimationFrame(tick);
    };

    rafRef.current = requestAnimationFrame(tick);
    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, [active]);

  // Snap to 100 % the moment the backend confirms.
  useEffect(() => {
    if (isReady) {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      setProgress(100);
      setStageIndex(STAGES.length - 1);
      setShowLongRunningHint(false);
    }
  }, [isReady]);

  if (!active) return null;

  const { Icon, label } = STAGES[stageIndex] || STAGES[0];
  const stageKey = isReady ? 'ready' : `stage-${stageIndex}`;
  const displayProgress = Math.floor(progress);

  return (
    <div
      data-testid="analysis-loading"
      className="bg-white rounded-2xl border border-[#E2DBCF] shadow-[0_30px_60px_-30px_rgba(50,35,20,0.18)] px-7 py-12 sm:px-12 sm:py-16"
    >
      <div className="max-w-xl mx-auto flex flex-col items-center">
        {/* Eyebrow */}
        <p className="font-['IBM_Plex_Mono'] text-[10px] sm:text-[11px] uppercase tracking-[0.3em] text-[#2C363F] opacity-70 mb-8">
          {isReady ? 'Analyse terminée' : 'Analyse en cours'}
        </p>

        {/* Icon halo */}
        <div className="relative mb-7">
          <span
            aria-hidden="true"
            className="absolute inset-0 rounded-full bg-[#E3D5CA] opacity-60 animate-ping-soft"
          />
          <div className="relative w-16 h-16 rounded-full bg-[#F0EBE1] border border-[#E2DBCF] flex items-center justify-center">
            <Icon
              key={`icon-${stageKey}`}
              className="h-7 w-7 text-[#1A2530] animate-fade-in"
              strokeWidth={1.5}
              aria-hidden="true"
            />
          </div>
        </div>

        {/* Stage label — the main visual element */}
        <h2
          key={`label-${stageKey}`}
          data-testid="analysis-stage-label"
          className="font-['Cormorant_Garamond'] font-light text-[26px] sm:text-[32px] text-[#1A2530] text-center leading-[1.2] mb-3 animate-fade-in min-h-[2.4em]"
          style={{ letterSpacing: '-0.01em' }}
          aria-live="polite"
        >
          {isReady ? 'Votre analyse est prête' : label}
        </h2>

        {/* Helper sub-text */}
        <p
          key={`sub-${stageKey}-${isUploading}-${showLongRunningHint}`}
          className="font-['Work_Sans'] text-[14px] text-[#2C363F] text-center max-w-md leading-relaxed opacity-90 mb-10 animate-fade-in min-h-[2.6em]"
        >
          {isReady
            ? 'Ouverture de votre dossier…'
            : isUploading
            ? 'Téléversement sécurisé de votre document.'
            : showLongRunningHint
            ? 'Les documents volumineux ou complexes peuvent nécessiter quelques secondes supplémentaires.'
            : 'HÉRITA analyse votre document. Vous pouvez patienter ici en toute confiance.'}
        </p>

        {/* Progress bar */}
        <div className="w-full max-w-md">
          <div className="relative h-1.5 bg-[#E2DBCF] rounded-full overflow-hidden">
            <div
              data-testid="analysis-progress-bar"
              className="absolute inset-y-0 left-0 bg-[#1A2530] rounded-full"
              style={{
                width: `${progress}%`,
                transition: 'width 600ms cubic-bezier(0.22, 1, 0.36, 1)',
              }}
            />
          </div>
          <div className="flex items-center justify-between mt-3">
            <p className="font-['Work_Sans'] text-[11px] text-[#2C363F] opacity-70 uppercase tracking-[0.18em]">
              {isReady ? 'Terminé' : 'Progression'}
            </p>
            <p
              data-testid="analysis-progress-percent"
              className="font-['IBM_Plex_Mono'] text-[11px] text-[#1A2530] opacity-60 tabular-nums"
            >
              {displayProgress}%
            </p>
          </div>
        </div>

        {/* Reassurance footer — privacy */}
        <div className="mt-10 pt-7 border-t border-[#E2DBCF] w-full max-w-md">
          <p className="font-['Work_Sans'] text-[12px] text-[#2C363F] text-center leading-relaxed opacity-75">
            Votre document reste strictement confidentiel.
            <br />
            Aucune copie n'est conservée après l'analyse.
          </p>
        </div>
      </div>

      {/* Scoped CSS for the soft animations we use above. Kept local on
          purpose so it cannot leak to the rest of the app. */}
      <style>{`
        @keyframes fade-in {
          from { opacity: 0; transform: translateY(4px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        .animate-fade-in {
          animation: fade-in 480ms cubic-bezier(0.22, 1, 0.36, 1);
        }
        @keyframes ping-soft {
          0%   { transform: scale(1);    opacity: 0.45; }
          70%  { transform: scale(1.45); opacity: 0;    }
          100% { transform: scale(1.45); opacity: 0;    }
        }
        .animate-ping-soft {
          animation: ping-soft 2.4s cubic-bezier(0, 0, 0.2, 1) infinite;
        }
      `}</style>
    </div>
  );
};

export default AnalysisLoadingScreen;
